import React, { useState } from 'react';
import { InputNumber, Select, Typography, Flex } from 'antd';
import { FilterDropdownProps } from 'antd/lib/table/interface';

import WidgetFooter from './WidgetFooter';

const enum SelectOptions {
  Equals = 'equals',
  Between = 'between',
}

export type StatusCodeFilter = Partial<{
  statusCodeLow: number;
  statusCodeHigh: number;
  statusCodeEq: number;
}>;

const QUERY_OPTIONS = [
  { value: SelectOptions.Equals, label: 'Equals' },
  { value: SelectOptions.Between, label: 'Between' },
];

type StatusDropdownFilterProps = Pick<FilterDropdownProps, 'confirm' | 'clearFilters'> & {
  selectedKeys: StatusCodeFilter[];
  setSelectedKeys: (keys: StatusCodeFilter[]) => void;
};

function StatusDropdownFilter({ confirm, selectedKeys, setSelectedKeys, clearFilters }: StatusDropdownFilterProps) {
  const statusCode = selectedKeys.length ? selectedKeys[0] : {};

  const [selectedValue, setSelectedValue] = useState<SelectOptions>(
    !!statusCode.statusCodeHigh || !!statusCode.statusCodeLow ? SelectOptions.Between : SelectOptions.Equals
  );

  const onReset = () => clearFilters?.();

  const resetDisabled = React.useMemo(() => {
    if (selectedValue === SelectOptions.Equals) return !statusCode.statusCodeEq;
    return !statusCode.statusCodeLow && !statusCode.statusCodeHigh;
  }, [selectedValue, statusCode]);

  React.useEffect(() => {
    return () => confirm();
  }, []);

  return (
    <Flex vertical style={{ padding: 12 }}>
      <Typography.Paragraph>HTTP Status Code</Typography.Paragraph>
      <Flex gap="small" align="center">
        <Select
          aria-label="Status code condition"
          onChange={(value) => setSelectedValue(value)}
          options={QUERY_OPTIONS}
          value={selectedValue}
          style={{ width: 120 }}
          virtual={false}
        />
        {selectedValue === SelectOptions.Between && (
          <>
            <InputNumber
              min={100}
              max={599}
              placeholder="200"
              value={statusCode.statusCodeLow}
              onChange={(value) => {
                if (value) setSelectedKeys([{ statusCodeLow: value, statusCodeHigh: statusCode.statusCodeHigh }]);
                else setSelectedKeys([{ statusCodeHigh: statusCode.statusCodeHigh }]);
              }}
            />
            <Typography.Text>and</Typography.Text>
            <InputNumber
              min={100}
              max={599}
              placeholder="499"
              value={statusCode.statusCodeHigh}
              onChange={(value) => {
                if (value) setSelectedKeys([{ statusCodeHigh: value, statusCodeLow: statusCode.statusCodeLow }]);
                else setSelectedKeys([{ statusCodeLow: statusCode.statusCodeLow }]);
              }}
            />
          </>
        )}
        {selectedValue === SelectOptions.Equals && (
          <InputNumber
            min={100}
            max={599}
            placeholder="200"
            value={statusCode.statusCodeEq}
            onChange={(value) => {
              if (value) setSelectedKeys([{ statusCodeEq: value }]);
              else setSelectedKeys([]);
            }}
          />
        )}
      </Flex>
      <WidgetFooter onReset={onReset} onOk={() => confirm()} resetDisabled={resetDisabled} />
    </Flex>
  );
}

export default StatusDropdownFilter;
